import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, User, Zap, Accessibility, Smartphone } from "lucide-react";
import { PrimaryCTA } from "@/components/PrimaryCTA";
import { Link } from "react-router-dom";
import platformsHero from "@/assets/blog/code-matrix-dark.jpg";

const BlogPlatforms = () => {
  const relatedArticles = [
    { title: "Why SEO is Dead", tag: "AI", icon: Zap },
    { title: "The 0.5-Second Rule", tag: "Speed", icon: Zap },
    { title: "Stop Losing Leads on Mobile", tag: "Mobile", icon: Smartphone },
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      <Helmet>
        <title>Wix vs Custom Code | Insights for South Wales Trades</title>
        <meta name="description" content="The true cost of drag-and-drop website builders vs custom-coded sites. Learn why templates kill conversion rates for trades." />
        <link rel="canonical" href="https://smartsite-lead-gen.lovable.app/blog-platforms" />
      </Helmet>
      <Navbar />
      
      <main className="pt-24 pb-20">
        <div className="container mx-auto px-4">
          <Breadcrumbs items={[
            { label: "Home", href: "/" },
            { label: "Insights", href: "/insights" },
            { label: "Wix vs. Custom Code", href: "/blog-platforms" }
          ]} />
          
          {/* Section 1: Article Header */}
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center mb-12"
          >
            <Badge className="bg-gradient-to-r from-purple-500 to-purple-600 text-white border-0 px-4 py-1.5 text-sm font-medium rounded-full mb-6">
              Tech Stack
            </Badge>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Wix vs. Custom Code: The True Cost of{" "}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                "Cheap"
              </span>{" "}
              Websites
            </h1>
            
            <div className="flex items-center justify-center gap-4 text-slate-400">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>By SmartSite Team</span>
              </div>
              <span>•</span>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>5 min read</span>
              </div>
            </div>
          </motion.header>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-5xl mx-auto mb-16"
          >
            <div className="aspect-video rounded-2xl overflow-hidden border border-white/10 relative">
              <img 
                src={platformsHero}
                alt="Code on screen representing custom web development versus template builders"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1024px"
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Heavy dark overlay for text readability */}
              <div className="absolute inset-0 bg-black/70" />
              {/* Accent glow overlays */}
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(168,85,247,0.12),transparent_50%)]" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,rgba(34,211,238,0.08),transparent_50%)]" />
            </div>
          </motion.div>

          {/* Section 2: Two Column Layout */}
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-[1fr_380px] gap-8">
              
              {/* Left Column - Article Content (70%) */}
              <motion.article
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <Card className="bg-white/5 backdrop-blur-xl border-white/10 rounded-2xl overflow-hidden">
                  <CardContent className="p-8 md:p-12">
                    {/* Intro */}
                    <p className="text-xl text-slate-300 leading-relaxed mb-10 font-light">
                      DIY builders like Wix and Squarespace look easy, but you're renting, not owning. You're trapped in a closed ecosystem with limited SEO capabilities. The moment you stop paying, your website disappears.
                    </p>

                    {/* Subhead 1 */}
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                      The "Drag and Drop"{" "}
                      <span className="bg-gradient-to-r from-red-400 to-red-500 bg-clip-text text-transparent">
                        Trap
                      </span>
                    </h2>
                    <p className="text-slate-400 leading-relaxed mb-10">
                      Every element you drag onto a Wix page generates layers of bloated, inefficient code. Google's crawlers struggle to parse this mess, which means your carefully written content gets buried. What looks simple on the surface is a technical nightmare underneath—and you're paying a monthly fee for the privilege of owning nothing.
                    </p>

                    {/* Subhead 2 */}
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                      Hand-Coded{" "}
                      <span className="bg-gradient-to-r from-purple-400 to-purple-500 bg-clip-text text-transparent">
                        Semantic HTML
                      </span>
                    </h2>
                    <p className="text-slate-400 leading-relaxed mb-10">
                      We write clean, semantic HTML that search engines love. Every heading, paragraph, and link is structured with purpose. There's no bloat, no hidden iframes, no mystery scripts. The result is a website that loads faster, ranks higher, and is 100% owned by you. You can take it anywhere, host it yourself, or sell it—it's your asset.
                    </p>

                    {/* Subhead 3 */}
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                      True{" "}
                      <span className="bg-gradient-to-r from-orange-400 to-orange-500 bg-clip-text text-transparent">
                        Ownership
                      </span>
                    </h2>
                    <p className="text-slate-400 leading-relaxed mb-10">
                      With a custom-coded website, you receive the complete source code. No vendor lock-in. No surprise price increases. No panic when the platform decides to "sunset" a feature you depend on. Your website becomes a business asset that appreciates over time, not a recurring liability.
                    </p>

                    {/* Conclusion */}
                    <div className="border-l-4 border-purple-500 pl-6 py-2">
                      <p className="text-lg text-white font-medium italic">
                        "Cheap" websites cost more in the long run. Invest in a custom asset that you own, control, and can grow with your business.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.article>

              {/* Right Column - Sticky Sidebar (30%) */}
              <motion.aside
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="space-y-6"
              >
                <div className="lg:sticky lg:top-28 space-y-6">
                  {/* Author Bio Card */}
                  <Card className="bg-white/5 backdrop-blur-xl border-white/10 rounded-2xl overflow-hidden">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 flex items-center justify-center text-white font-bold text-lg">
                          SS
                        </div>
                        <div>
                          <h3 className="text-white font-semibold">SmartSite Solutions</h3>
                          <p className="text-slate-400 text-sm">North Cornelly</p>
                        </div>
                      </div>
                      <p className="text-slate-400 text-sm leading-relaxed">
                        Engineering growth for South Wales trades.
                      </p>
                    </CardContent>
                  </Card>

                  {/* CTA Card */}
                  <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 backdrop-blur-xl border-purple-500/20 rounded-2xl overflow-hidden">
                    <CardContent className="p-6 text-center">
                      <h3 className="text-xl font-bold text-white mb-3">
                        Stop Renting.
                      </h3>
                      <p className="text-slate-400 text-sm mb-6">
                        Own your website. Own your future. Get a custom-coded asset built for your business.
                      </p>
                      <PrimaryCTA 
                        text="Get a Custom Asset" 
                        href="/contact" 
                        external={false}
                        fullWidth
                      />
                    </CardContent>
                  </Card>

                  {/* Services Links Card */}
                  <Card className="bg-white/5 backdrop-blur-xl border-white/10 rounded-2xl overflow-hidden">
                    <CardContent className="p-6">
                      <h3 className="text-white font-semibold mb-4">Our Services</h3>
                      <ul className="space-y-3">
                        <li>
                          <Link 
                            to="/services" 
                            className="text-slate-400 hover:text-orange-400 transition-colors flex items-center gap-2 group"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-orange-500 group-hover:scale-125 transition-transform" />
                            Web Design
                          </Link>
                        </li>
                        <li>
                          <Link 
                            to="/services" 
                            className="text-slate-400 hover:text-orange-400 transition-colors flex items-center gap-2 group"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 group-hover:scale-125 transition-transform" />
                            Local SEO
                          </Link>
                        </li>
                        <li>
                          <Link 
                            to="/services" 
                            className="text-slate-400 hover:text-orange-400 transition-colors flex items-center gap-2 group"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 group-hover:scale-125 transition-transform" />
                            Care Plans
                          </Link>
                        </li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </motion.aside>
            </div>
          </div>

          {/* Section 3: Related Articles */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="max-w-6xl mx-auto mt-20"
          >
            <h2 className="text-3xl font-bold text-white mb-8">
              More{" "}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Insights
              </span>
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {relatedArticles.map((article, index) => (
                <motion.div
                  key={article.tag}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.6 + index * 0.1 }}
                >
                  <Card className="bg-white/5 backdrop-blur-xl border-white/10 rounded-2xl overflow-hidden hover:border-white/20 transition-all duration-300 group cursor-pointer h-full">
                    <CardContent className="p-6">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-slate-700 to-slate-800 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform border border-white/10">
                        <article.icon className="w-6 h-6 text-purple-400" />
                      </div>
                      <Badge variant="outline" className="border-white/20 text-slate-400 mb-3">
                        {article.tag}
                      </Badge>
                      <h3 className="text-lg font-semibold text-white group-hover:text-orange-400 transition-colors">
                        {article.title}
                      </h3>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPlatforms;
